import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { SafeArea } from 'capacitor-plugin-safe-area';

const ANDROID_THREE_BUTTON_NAVIGATION_FIXED_OFFSET = 24;

interface SetMobileSafeAreaOptions {
  androidEdgeToEdge?: boolean;
}

let lastOptions: SetMobileSafeAreaOptions | undefined;
let isAutoRefreshConfigured = false;

export async function mobileSafeArea() {
  const res = await SafeArea.getSafeAreaInsets();

  return {
    top: res.insets.top,
    bottom: res.insets.bottom,
    left: res.insets.left,
    right: res.insets.right,
  };
}

async function shouldApplyAndroidBottomInset(
  options?: SetMobileSafeAreaOptions
) {
  if (Capacitor.getPlatform() !== 'android') return false;

  if (typeof options?.androidEdgeToEdge === 'boolean') {
    return options.androidEdgeToEdge;
  }

  const info = await Device.getInfo();

  return Boolean(info.androidSDKVersion && info.androidSDKVersion >= 35);
}

async function applyMobileSafeArea(options?: SetMobileSafeAreaOptions) {
  const res = await mobileSafeArea();

  const isAndroid = Capacitor.getPlatform() === 'android';
  const isAndroidEdgeToEdge = await shouldApplyAndroidBottomInset(options);
  const hasAndroidNavigationInset =
    isAndroid && isAndroidEdgeToEdge && res.bottom > 0;
  let bottom = Math.max(res.bottom - 8, 0);

  if (isAndroid && !isAndroidEdgeToEdge) {
    bottom = 0;
  }

  if (hasAndroidNavigationInset) {
    bottom = ANDROID_THREE_BUTTON_NAVIGATION_FIXED_OFFSET;
  }

  const top = Math.max(res.top - 8, 0);
  const fixedBottomOffset = hasAndroidNavigationInset
    ? ANDROID_THREE_BUTTON_NAVIGATION_FIXED_OFFSET
    : 0;
  const fixedBottomPadding = isAndroid ? 16 : bottom + 16;

  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty(
      '--safe-area-inset-top',
      `${top}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-inset-bottom',
      `${bottom}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-fixed-bottom-offset',
      `${fixedBottomOffset}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-fixed-bottom-padding',
      `${fixedBottomPadding}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-inset-left',
      `${res.left}px`
    );
    document.documentElement.style.setProperty(
      '--safe-area-inset-right',
      `${res.right}px`
    );
  }
}

function scheduleMobileSafeAreaRefresh(options?: SetMobileSafeAreaOptions) {
  if (typeof window === 'undefined') return;

  [50, 250, 750].forEach((delay) => {
    window.setTimeout(() => {
      void applyMobileSafeArea(options);
    }, delay);
  });
}

function configureMobileSafeAreaAutoRefresh() {
  if (isAutoRefreshConfigured) return;

  isAutoRefreshConfigured = true;

  void App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      scheduleMobileSafeAreaRefresh(lastOptions);
    }
  });

  if (typeof window === 'undefined') return;

  window.addEventListener('resize', () => {
    scheduleMobileSafeAreaRefresh(lastOptions);
  });

  window.addEventListener('orientationchange', () => {
    scheduleMobileSafeAreaRefresh(lastOptions);
  });
}

export async function setMobileSafeArea(options?: SetMobileSafeAreaOptions) {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  lastOptions = options;
  configureMobileSafeAreaAutoRefresh();

  await applyMobileSafeArea(options);
  scheduleMobileSafeAreaRefresh(options);
}
